(() => {
  if (customElements.get('cart-drawer')) return;

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.onCartAdd = this.onCartAdd.bind(this);
      this.onCartChange = this.onCartChange.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onCartIconClick = this.onCartIconClick.bind(this);
      this.onCollectionCartChange = this.onCollectionCartChange.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.activeLineKeys = new Set();
      this._collectionSynced = false;
      this._termsAccepted = false;

      this.addEventListener('click', (e) => this.onDelegatedClick(e));
      this.addEventListener('change', (e) => this.onDelegatedChange(e));
    }

    connectedCallback() {
      window.addEventListener('cart:add', this.onCartAdd);
      window.addEventListener('cart:change', this.onCartChange);
      this.registerLineInventory();

      this.cartIcons = document.querySelectorAll('.header__cart');
      this.cartIcons.forEach((icon) => {
        icon.addEventListener('click', this.onCartIconClick);
      });

      this._restoreTermsState();

      this._observer = new MutationObserver(() => {
        this._restoreTermsState();
        this.registerLineInventory();
      });
      this._observer.observe(this, { childList: true, subtree: true });

      this._collectionHandle = this.dataset.collectionHandle || '';
      this._collectionTitle = this.dataset.collectionTitle || '';

      if (this.usesCollectionCart()) {
        window.DOCollectionCart.setActiveCollection(this._collectionHandle, this._collectionTitle);
        window.DOCollectionCart.reconcileFromCookie();
        this.updateBadgeFromLocal();
        window.addEventListener('collection-cart:change', this.onCollectionCartChange);
        window.addEventListener('pageshow', this.onPageShow);
        window.DOCollectionCart.ensureShopifySync()
          .then(() => this.updateBadgeFromLocal())
          .catch(() => {});
      }
    }

    disconnectedCallback() {
      this._observer?.disconnect();
      window.removeEventListener('cart:add', this.onCartAdd);
      window.removeEventListener('cart:change', this.onCartChange);
      document.removeEventListener('keydown', this.onKeyDown);
      this.cartIcons?.forEach((icon) => {
        icon.removeEventListener('click', this.onCartIconClick);
      });
      window.removeEventListener('collection-cart:change', this.onCollectionCartChange);
      window.removeEventListener('pageshow', this.onPageShow);
    }

    onDelegatedClick(e) {
      if (e.target.closest('.js-cart-drawer-overlay') || e.target.closest('.js-cart-drawer-close')) {
        this.close();
        return;
      }

      const removeBtn = e.target.closest('.js-cart-drawer-remove');
      if (removeBtn) {
        this.onRemoveClick(removeBtn);
        return;
      }

      const qtyBtn = e.target.closest('.js-cart-drawer-qty');
      if (qtyBtn) {
        this.onQtyClick(qtyBtn);
        return;
      }

      const checkoutLink = e.target.closest('.cart-drawer__checkout');
      if (checkoutLink) {
        this.onCheckoutClick(e, checkoutLink);
      }
    }

    onDelegatedChange(e) {
      const checkbox = e.target.closest('.js-cart-drawer-terms-checkbox');
      if (checkbox) {
        this.onTermsChange(checkbox);
      }
    }

    onCollectionCartChange() {
      this.updateBadgeFromLocal();
    }

    async onPageShow(e) {
      if (!e.persisted || !this.usesCollectionCart()) return;
      window.DOCollectionCart.reloadState();
      window.DOCollectionCart.reconcileFromCookie();
      this.updateBadgeFromLocal();
      window.DOCollectionCart.invalidateSync();
      this._collectionSynced = false;
      try {
        await this._ensureCollectionSync();
      } catch {}
      this.updateBadgeFromLocal();
    }

    onCartIconClick(e) {
      e.preventDefault();
      this.open();
    }

    onCartAdd(e) {
      const sections = e.detail?.sections;
      if (sections?.['cart-drawer']) {
        this.updateContent(sections['cart-drawer']);
      }

      if (this.usesCollectionCart()) {
        this._collectionSynced = true;
        this._saveCartToLocal();
      }

      this.updateBadge();
      this.open();
    }

    onCartChange(e) {
      const sections = e.detail?.sections;
      if (sections?.['cart-drawer']) {
        this.updateContent(sections['cart-drawer']);
      }
      this.updateBadge(e.detail?.cart);
    }

    async open() {
      try {
        if (this.usesCollectionCart() && !this._collectionSynced) {
          await this._ensureCollectionSync();
        }
      } catch {}

      this.triggerElement = document.activeElement;
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      this.classList.add('is-open');
      document.addEventListener('keydown', this.onKeyDown);
      this.querySelector('.js-cart-drawer-close')?.focus();
    }

    close() {
      this.classList.remove('is-open');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.removeEventListener('keydown', this.onKeyDown);
      this.triggerElement?.focus();
    }

    onKeyDown(e) {
      if (e.key === 'Escape') {
        this.close();
        return;
      }
      if (e.key === 'Tab') {
        this.trapFocus(e);
      }
    }

    trapFocus(e) {
      const focusable = this.querySelector('.cart-drawer__panel')?.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    async onQtyClick(btn) {
      const stepper = btn.closest('[data-line-key]');
      if (!stepper) return;

      const lineKey = stepper.dataset.lineKey;
      const action = btn.dataset.action;
      const valueEl = stepper.querySelector('.cart-drawer__qty-value');
      const currentQty = parseInt(valueEl.textContent, 10);
      const item = btn.closest('.cart-drawer__item');

      let newQty = currentQty;
      if (action === 'increase') {
        newQty = currentQty + 1;
      } else if (action === 'decrease' && currentQty > 1) {
        newQty = currentQty - 1;
      }

      if (newQty === currentQty) return;
      if (this.activeLineKeys.has(lineKey)) return;

      const prevQty = currentQty;
      const decreaseBtn = stepper.querySelector('[data-action="decrease"]');
      this.activeLineKeys.add(lineKey);
      this.clearLineError(item);
      item?.classList.add('is-loading');

      try {
        if (action === 'increase') {
          const validation = await this.validateQuantityChange(lineKey, newQty);
          if (!validation.valid) {
            this.showLineError(item, this.getInventoryErrorMessage(item));
            return;
          }
        }

        valueEl.textContent = newQty;
        if (decreaseBtn) decreaseBtn.disabled = newQty <= 1;
        await this.changeQuantity(lineKey, newQty);
      } catch (error) {
        valueEl.textContent = prevQty;
        if (decreaseBtn) decreaseBtn.disabled = prevQty <= 1;
        this.showLineError(item, error.message || this.dataset.errorMessage || 'Could not update cart.');
      } finally {
        this.activeLineKeys.delete(lineKey);
        item?.classList.remove('is-loading');
      }
    }

    async onRemoveClick(btn) {
      const lineKey = btn.dataset.lineKey;
      if (!lineKey) return;

      const item = btn.closest('.cart-drawer__item');
      item?.classList.add('is-loading');
      this.clearLineError(item);
      btn.disabled = true;

      try {
        await this.changeQuantity(lineKey, 0);
      } catch (error) {
        item?.classList.remove('is-loading');
        btn.disabled = false;
        this.showLineError(item, error.message || this.dataset.errorMessage || 'Could not update cart.');
      }
    }

    registerLineInventory() {
      const script = this.querySelector('.js-cart-drawer-inventory-data');
      window.DOBundleAvailability?.registerVariantsFromJson(script?.textContent || '');
    }

    async validateQuantityChange(lineKey, quantity) {
      if (typeof window.DOBundleAvailability?.validateLineQuantityChange !== 'function') {
        return { valid: true, failures: [] };
      }

      return window.DOBundleAvailability.validateLineQuantityChange(lineKey, quantity);
    }

    getInventoryErrorMessage(item) {
      if (item?.querySelector('.cart-drawer__item-bundle-list')) {
        return this.dataset.bundleInventoryMessage || this.dataset.inventoryMessage || this.dataset.errorMessage;
      }

      return this.dataset.inventoryMessage || this.dataset.errorMessage;
    }

    getLineErrorEl(item) {
      return item?.querySelector('.js-cart-drawer-line-error') || null;
    }

    clearLineError(item) {
      const errorEl = this.getLineErrorEl(item);
      if (!errorEl) return;

      errorEl.textContent = '';
      errorEl.hidden = true;
    }

    showLineError(item, message) {
      const errorEl = this.getLineErrorEl(item);
      if (!errorEl || !message) return;

      errorEl.textContent = message;
      errorEl.hidden = false;
    }

    async changeQuantity(lineKey, quantity) {
      const root = window.Shopify?.routes?.root ?? '/';
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          id: lineKey,
          quantity,
          sections: 'cart-drawer',
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.description || data.message || this.dataset.errorMessage || 'Cart update failed');
      }

      if (this.usesCollectionCart() && data.items) {
        window.DOCollectionCart.saveFromShopifyCart(data);
      }

      if (data.sections?.['cart-drawer']) {
        this.updateContent(data.sections['cart-drawer']);
      }
      this.updateBadge(data);
      window.dispatchEvent(new CustomEvent('cart:change', { detail: { cart: data, sections: data.sections } }));
    }

    updateContent(html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const newBody = parsed.querySelector('.cart-drawer__body');
      const newFooter = parsed.querySelector('.cart-drawer__footer');
      const currentBody = this.querySelector('.cart-drawer__body');
      const currentFooter = this.querySelector('.cart-drawer__footer');

      if (newBody && currentBody) currentBody.innerHTML = newBody.innerHTML;

      if (newFooter) {
        if (currentFooter) {
          currentFooter.replaceWith(newFooter.cloneNode(true));
        } else {
          this.querySelector('.cart-drawer__panel')?.appendChild(newFooter.cloneNode(true));
        }
      } else if (currentFooter) {
        currentFooter.remove();
      }

      this.registerLineInventory();
      this._restoreTermsState();
    }

    async updateBadge(cart = null) {
      const root = window.Shopify?.routes?.root ?? '/';
      try {
        let currentCart = cart;
        if (!currentCart || typeof currentCart.item_count !== 'number') {
          if (typeof window.DOBundleAvailability?.getCart === 'function') {
            currentCart = await window.DOBundleAvailability.getCart({ maxAge: 0 });
          } else {
            currentCart = await fetch(`${root}cart.js`, {
              headers: { 'X-Requested-With': 'XMLHttpRequest' },
            }).then((r) => r.json());
          }
        }

        if (this.usesCollectionCart() && currentCart?.items) {
          window.DOCollectionCart.saveFromShopifyCart(currentCart);
        }

        document.querySelectorAll('.header__cart-count').forEach((badge) => {
          badge.textContent = currentCart.item_count;
          badge.hidden = currentCart.item_count <= 0;
        });
      } catch {}
    }

    usesCollectionCart() {
      return Boolean(this._collectionHandle && window.DOCollectionCart);
    }

    updateBadgeFromLocal() {
      if (!this.usesCollectionCart()) return;
      const count = window.DOCollectionCart.getCart().itemCount;
      document.querySelectorAll('.header__cart-count').forEach((badge) => {
        badge.textContent = count;
        badge.hidden = count <= 0;
      });
    }

    _restoreTermsState() {
      const checkbox = this.querySelector('.js-cart-drawer-terms-checkbox');
      if (checkbox) checkbox.checked = this._termsAccepted;
      this._syncCheckoutDisabled();
    }

    onTermsChange(checkbox) {
      this._termsAccepted = Boolean(checkbox.checked);
      if (this._termsAccepted) {
        checkbox.closest('.js-cart-drawer-terms')?.classList.remove('is-error');
      }
      this._syncCheckoutDisabled();
    }

    _syncCheckoutDisabled() {
      const checkoutLink = this.querySelector('.cart-drawer__checkout');
      if (!checkoutLink) return;
      const blocked = Boolean(this.querySelector('.js-cart-drawer-terms-checkbox')) && !this._termsAccepted;
      checkoutLink.classList.toggle('is-disabled', blocked);
      if (blocked) {
        checkoutLink.setAttribute('aria-disabled', 'true');
      } else {
        checkoutLink.removeAttribute('aria-disabled');
      }
    }

    async onCheckoutClick(e, link) {
      const terms = this.querySelector('.js-cart-drawer-terms-checkbox');
      if (terms && !terms.checked) {
        e.preventDefault();
        terms.closest('.js-cart-drawer-terms')?.classList.add('is-error');
        terms.focus();
        return;
      }
      if (!this.usesCollectionCart()) return;
      e.preventDefault();
      const href = link.href || '/checkout';
      try {
        await window.DOCollectionCart.ensureShopifySync();
      } catch {}
      window.location.href = href;
    }

    async _ensureCollectionSync() {
      if (this._collectionSynced) return;
      await window.DOCollectionCart.ensureShopifySync();
      await this._fetchAndUpdateSection();
      this._collectionSynced = true;
    }

    async _fetchAndUpdateSection() {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('sections', 'cart-drawer');
        const response = await fetch(url.toString(), {
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });
        if (!response.ok) return;
        const data = await response.json();
        if (data['cart-drawer']) {
          this.updateContent(data['cart-drawer']);
        }
      } catch {}
    }

    async _saveCartToLocal() {
      try {
        let cart;
        if (typeof window.DOBundleAvailability?.getCart === 'function') {
          cart = await window.DOBundleAvailability.getCart({ maxAge: 2000 });
        } else {
          const root = window.Shopify?.routes?.root ?? '/';
          cart = await fetch(`${root}cart.js`, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
          }).then((r) => r.json());
        }
        if (cart) {
          window.DOCollectionCart.saveFromShopifyCart(cart);
          this.updateBadgeFromLocal();
        }
      } catch {}
    }
  }

  customElements.define('cart-drawer', CartDrawer);
})();
